/**
 * CustomerWalletManagement — Full desktop wallet console
 *
 * Features:
 *   • All-customers directory with wallet balance, filters, sort
 *   • Type-ahead suggestions (name / phone / customer_code)
 *   • Inline Add-fund → creates pending entry (two-staff rule)
 *   • Inline Verify / Reject on pending rows
 *   • Below-min (₹1,000) and closed states
 *   • Print current view · Monthly wallet-usage report (print-ready)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import {
  Wallet, MagnifyingGlass, CheckCircle, Clock, Plus,
  ArrowsClockwise, User, CurrencyInr, ShieldCheck, Printer,
  Funnel, X, CaretDown, Warning, LockKey, Receipt,
} from "@phosphor-icons/react";

const MODES = ["cash", "upi", "card", "netbanking", "cheque", "neft", "other"];
const MIN_BOOK = 1000;

const SORTS = [
  { value: "balance_desc", label: "Balance · High → Low" },
  { value: "balance_asc", label: "Balance · Low → High" },
  { value: "name", label: "Name · A → Z" },
  { value: "updated_at", label: "Recently updated" },
];
const WALLET_STATES = [
  { value: "", label: "All wallets" },
  { value: "below_min", label: "Below ₹1,000 (blocked)" },
  { value: "positive", label: "Positive balance" },
  { value: "zero", label: "Zero balance" },
  { value: "closed", label: "Closed wallets" },
];
const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "pending_verification", label: "Pending verification" },
  { value: "rejected", label: "Rejected" },
];

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function CustomerWalletManagement() {
  // Directory state
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ count: 0, total_balance: 0, below_min_count: 0, closed_count: 0 });
  const [loading, setLoading] = useState(false);

  // Filters
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [districtId, setDistrictId] = useState("");
  const [lsgdId, setLsgdId] = useState("");
  const [wardId, setWardId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [walletState, setWalletState] = useState("");
  const [sortBy, setSortBy] = useState("balance_desc");

  // Location masters
  const [districts, setDistricts] = useState([]);
  const [lsgds, setLsgds] = useState([]);
  const [wards, setWards] = useState([]);

  // Pending queue
  const [pending, setPending] = useState([]);

  // Add-fund modal
  const [fundFor, setFundFor] = useState(null); // customer row
  const [fundForm, setFundForm] = useState(blankFund());
  const [fundBusy, setFundBusy] = useState(false);

  // Monthly report modal
  const now = new Date();
  const [reportOpen, setReportOpen] = useState(false);
  const [reportY, setReportY] = useState(now.getFullYear());
  const [reportM, setReportM] = useState(now.getMonth() + 1);
  const [reportData, setReportData] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const reportRef = useRef(null);

  function blankFund() {
    return {
      amount: "", collection_date: new Date().toISOString().slice(0, 10),
      transaction_mode: "cash", transaction_id: "", notes: "",
    };
  }

  // Load districts once
  useEffect(() => {
    api.get("/locations?type=district")
      .then(r => setDistricts(r.data || []))
      .catch(() => setDistricts([]));
  }, []);
  useEffect(() => {
    if (!districtId) { setLsgds([]); setLsgdId(""); setWards([]); setWardId(""); return; }
    api.get(`/locations?type=lsgd&parent_id=${districtId}`)
      .then(r => setLsgds(r.data || [])).catch(() => setLsgds([]));
    setLsgdId(""); setWards([]); setWardId("");
  }, [districtId]);
  useEffect(() => {
    if (!lsgdId) { setWards([]); setWardId(""); return; }
    api.get(`/locations?type=ward&parent_id=${lsgdId}`)
      .then(r => setWards(r.data || [])).catch(() => setWards([]));
    setWardId("");
  }, [lsgdId]);

  const loadDirectory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (districtId) params.set("district_id", districtId);
      if (lsgdId) params.set("lsgd_id", lsgdId);
      if (wardId) params.set("ward_id", wardId);
      if (statusFilter) params.set("status", statusFilter);
      if (walletState) params.set("wallet_state", walletState);
      if (sortBy) params.set("sort_by", sortBy);
      const r = await api.get(`/crm/wallet/customers?${params.toString()}`);
      setRows(r.data.customers || []);
      setTotals(r.data.totals || { count: 0, total_balance: 0, below_min_count: 0, closed_count: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadPending = async () => {
    try {
      const r = await api.get("/crm/wallet/pending");
      setPending(r.data.pending || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadDirectory(); }, [districtId, lsgdId, wardId, statusFilter, walletState, sortBy]);
  useEffect(() => { loadPending(); }, []);

  // Debounced suggestions on q
  useEffect(() => {
    if (!q.trim() || q.trim().length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/customers?q=${encodeURIComponent(q.trim())}`);
        setSuggestions((r.data || []).slice(0, 8));
        setShowSuggest(true);
      } catch { setSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const applySearch = () => { setShowSuggest(false); loadDirectory(); };
  const clearFilters = () => {
    setQ(""); setDistrictId(""); setLsgdId(""); setWardId("");
    setStatusFilter(""); setWalletState(""); setSortBy("balance_desc");
  };

  const openFund = (row) => { setFundFor(row); setFundForm(blankFund()); };
  const submitFund = async () => {
    const amt = Number(fundForm.amount);
    if (!amt || amt < 1) return toast.error("Amount required");
    if (!fundForm.collection_date) return toast.error("Collection date required");
    setFundBusy(true);
    try {
      await api.post("/crm/wallet/staff-credit", {
        customer_id: fundFor.id,
        amount: amt,
        collection_date: fundForm.collection_date,
        transaction_mode: fundForm.transaction_mode,
        transaction_id: fundForm.transaction_id.trim() || null,
        notes: fundForm.notes.trim() || null,
      });
      toast.success("Pending verification created — another staff must verify");
      setFundFor(null); loadPending();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setFundBusy(false); }
  };

  const verifyPending = async (tid) => {
    try {
      await api.post(`/crm/wallet/${tid}/verify`);
      toast.success("Verified & credited");
      loadPending(); loadDirectory();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const rejectPending = async (tid) => {
    if (!window.confirm("Reject this entry?")) return;
    try {
      await api.post(`/crm/wallet/${tid}/reject`);
      toast.success("Rejected");
      loadPending();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runReport = async () => {
    setReportBusy(true);
    try {
      const params = new URLSearchParams();
      params.set("year", String(reportY));
      params.set("month", String(reportM));
      if (districtId) params.set("district_id", districtId);
      if (lsgdId) params.set("lsgd_id", lsgdId);
      if (wardId) params.set("ward_id", wardId);
      const r = await api.get(`/crm/wallet/monthly-report?${params.toString()}`);
      setReportData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReportBusy(false); }
  };

  const printList = () => window.print();
  const printReport = () => {
    if (!reportRef.current) return;
    const w = window.open("", "_blank", "width=1000,height=700");
    if (!w) return toast.error("Popup blocked — allow popups to print");
    w.document.write(`<!doctype html><html><head><title>Wallet Usage · ${MONTHS[reportM - 1]} ${reportY}</title>
      <style>
        body{font-family:system-ui,sans-serif;padding:24px;color:#111827;}
        h1{margin:0 0 4px 0;font-size:22px;}
        .muted{color:#6B7280;font-size:12px;}
        table{width:100%;border-collapse:collapse;margin-top:16px;font-size:12px;}
        th,td{border:1px solid #E5E7EB;padding:6px 8px;text-align:left;}
        th{background:#F7F6F2;text-transform:uppercase;letter-spacing:0.1em;font-size:10px;color:#5C5A55;}
        .kpi{display:inline-block;padding:8px 14px;border:1px solid #E5E7EB;border-radius:6px;margin-right:8px;margin-top:8px;}
        .kpi b{display:block;font-size:16px;}
        .right{text-align:right;} .credit{color:#15803D;} .debit{color:#DC2626;}
      </style></head><body>${reportRef.current.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 300);
  };

  const kindLabel = (k) => (k || "").replace(/_/g, " ");

  return (
    <AppShell
      title="Wallet Management"
      subtitle="Customer Management · Prepaid wallet console"
      actions={
        <div className="flex gap-2 print:hidden">
          <button
            onClick={() => setReportOpen(true)}
            data-testid="wallet-open-report"
            className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]"
          >
            <Receipt size={12} weight="bold" /> Monthly report
          </button>
          <button
            onClick={printList}
            data-testid="wallet-print-list"
            className="inline-flex items-center gap-2 h-10 px-3 border border-[#E5E2D9] rounded-md text-xs hover:bg-[#F7F6F2]"
          >
            <Printer size={12} weight="bold" /> Print
          </button>
          <button
            onClick={() => { loadDirectory(); loadPending(); }}
            data-testid="wallet-refresh"
            className="inline-flex items-center gap-2 h-10 px-3 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-semibold"
          >
            <ArrowsClockwise size={12} weight="bold" /> Refresh
          </button>
        </div>
      }
    >
      <div className="space-y-5" data-testid="crm-wallet-mgmt">
        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard icon={User} label="Customers shown" value={totals.count} tone="slate" />
          <KpiCard icon={CurrencyInr} label="Total wallet ₹" value={`₹${totals.total_balance.toLocaleString("en-IN")}`} tone="red" />
          <KpiCard icon={Warning} label="Below ₹1,000" value={totals.below_min_count} sub="Cannot book SR" tone="amber" />
          <KpiCard icon={Clock} label="Pending verify" value={pending.length} sub="Two-staff queue" tone="green" />
        </div>

        {/* Pending strip — collapsible only when >0 */}
        {pending.length > 0 && (
          <div className="bg-white border border-[#F9E4B7] rounded-md overflow-hidden print:hidden">
            <div className="px-4 py-2 bg-[#FFF8E6] flex items-center gap-2">
              <Clock size={14} weight="fill" className="text-[#B45309]" />
              <div className="text-[11px] uppercase tracking-[0.18em] text-[#7A5A11] font-bold">
                Pending verifications ({pending.length})
              </div>
              <div className="text-[11px] text-[#7A5A11] ml-2 italic">
                Second staff (≠ collector) must verify to credit
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] bg-[#FAFAF7] border-b border-[#F0EEE7]">
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2">Amount</th>
                  <th className="px-4 py-2">Mode</th>
                  <th className="px-4 py-2">Collected</th>
                  <th className="px-4 py-2">Collected By</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pending.map((p, i) => (
                  <tr key={p.id} className={i % 2 ? "bg-[#FAFAF7]" : "bg-white"} data-testid={`crm-wallet-p-${p.id}`}>
                    <td className="px-4 py-2">
                      <div className="font-semibold text-[#111827]">{p.customer_name || p.customer_id}</div>
                      {p.notes && <div className="text-[11px] text-[#8A8780]">{p.notes}</div>}
                    </td>
                    <td className="px-4 py-2 font-mono font-bold text-[#DC2626]">₹{Number(p.amount || 0).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] rounded bg-[#F7F6F2] text-[#5C5A55] font-semibold">{p.transaction_mode}</span>
                      {p.transaction_id && <div className="text-[10px] font-mono text-[#8A8780] mt-0.5">#{p.transaction_id}</div>}
                    </td>
                    <td className="px-4 py-2 text-xs text-[#5C5A55]">{p.collection_date}</td>
                    <td className="px-4 py-2 text-xs text-[#5C5A55]">{p.collected_by_name}</td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => verifyPending(p.id)} data-testid={`crm-wallet-verify-${p.id}`}
                                className="h-8 px-3 bg-[#15803D] hover:bg-[#166534] text-white rounded-md text-xs font-semibold">
                          Verify
                        </button>
                        <button onClick={() => rejectPending(p.id)}
                                className="h-8 px-3 border border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2] rounded-md text-xs font-semibold">
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Filter bar */}
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden print:hidden">
          <div className="p-4 flex items-center gap-3 flex-wrap">
            {/* Search with suggestions */}
            <div className="relative flex-1 min-w-[260px] max-w-sm">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8780]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => q.trim().length >= 2 && setShowSuggest(true)}
                onBlur={() => setTimeout(() => setShowSuggest(false), 200)}
                onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
                placeholder="Search name, phone, customer ID…"
                data-testid="crm-wallet-search"
                className="w-full h-10 pl-9 pr-3 border border-[#E5E2D9] rounded-md text-sm focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626] outline-none"
              />
              {showSuggest && suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-11 z-20 border border-[#E5E2D9] rounded-md bg-white shadow-lg max-h-72 overflow-y-auto"
                     data-testid="crm-wallet-suggest">
                  {suggestions.map((s) => (
                    <button
                      key={s.id}
                      onMouseDown={(e) => { e.preventDefault(); setQ(s.name); setShowSuggest(false); }}
                      className="w-full text-left px-3 py-2 border-b border-[#F0EEE7] last:border-b-0 hover:bg-[#FEF7F6] flex items-center gap-2"
                      data-testid={`crm-wallet-suggest-${s.id}`}
                    >
                      <User size={14} className="text-[#8A8780] shrink-0" weight="bold" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-[#111827] truncate">{s.name}</div>
                        <div className="text-[11px] text-[#8A8780] truncate">
                          {s.phone || "—"} · {s.customer_code || s.id.slice(0, 8)}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* District / LSGD / Ward */}
            <FilterSelect value={districtId} onChange={setDistrictId} data-testid="filter-district"
                          options={[{ value: "", label: "All districts" }, ...districts.map(d => ({ value: d.id, label: d.name }))]}/>
            <FilterSelect value={lsgdId} onChange={setLsgdId} disabled={!districtId} data-testid="filter-lsgd"
                          options={[{ value: "", label: districtId ? "All LSGDs" : "Select district first" }, ...lsgds.map(l => ({ value: l.id, label: l.name }))]}/>
            <FilterSelect value={wardId} onChange={setWardId} disabled={!lsgdId} data-testid="filter-ward"
                          options={[{ value: "", label: lsgdId ? "All wards" : "Select LSGD first" }, ...wards.map(w => ({ value: w.id, label: w.name }))]}/>

            {/* Status */}
            <FilterSelect value={statusFilter} onChange={setStatusFilter} data-testid="filter-status"
                          options={STATUS_OPTIONS}/>

            {/* Wallet state */}
            <FilterSelect value={walletState} onChange={setWalletState} data-testid="filter-wallet-state"
                          options={WALLET_STATES}/>

            {/* Sort */}
            <FilterSelect value={sortBy} onChange={setSortBy} data-testid="filter-sort"
                          options={SORTS}/>

            <button onClick={applySearch}
                    className="h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-semibold">
              Apply
            </button>
            <button onClick={clearFilters}
                    className="h-10 px-3 border border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2] rounded-md text-xs inline-flex items-center gap-1">
              <X size={12} weight="bold" /> Clear
            </button>
          </div>
        </div>

        {/* Directory table */}
        <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E2D9] bg-[#FAFAF7] flex items-center gap-2 print:bg-white">
            <Wallet size={14} weight="bold" className="text-[#DC2626]" />
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold">
              Customer wallets ({rows.length})
            </div>
            <div className="ml-auto text-[11px] text-[#8A8780] print:hidden">
              Total on view: <b className="text-[#DC2626]">₹{totals.total_balance.toLocaleString("en-IN")}</b>
            </div>
          </div>

          {loading ? (
            <div className="py-14 text-center text-sm text-[#8A8780]">Loading customers…</div>
          ) : rows.length === 0 ? (
            <div className="py-14 text-center">
              <Funnel size={28} className="inline-block text-[#8A8780] mb-2" weight="duotone" />
              <div className="text-sm text-[#5C5A55]">No customers match the current filters.</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="wallet-directory-table">
                <thead>
                  <tr className="bg-white text-left text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] border-b border-[#F0EEE7]">
                    <th className="px-4 py-2">Customer</th>
                    <th className="px-4 py-2">Phone</th>
                    <th className="px-4 py-2">District · LSGD · Ward</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2 text-right">Balance</th>
                    <th className="px-4 py-2 text-right print:hidden">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={r.id}
                        className={`${i % 2 ? "bg-[#FAFAF7]" : "bg-white"} ${r.wallet_closed ? "opacity-70" : ""}`}
                        data-testid={`wallet-row-${r.id}`}>
                      <td className="px-4 py-2">
                        <div className="font-semibold text-[#111827] inline-flex items-center gap-2">
                          {r.name}
                          {r.wallet_closed && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded bg-[#F7F6F2] text-[#5C5A55] font-bold">
                              <LockKey size={9} weight="bold" /> closed
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#8A8780] font-mono">{r.customer_code || r.id.slice(0, 8)}</div>
                      </td>
                      <td className="px-4 py-2 text-xs font-mono text-[#5C5A55]">{r.phone || "—"}</td>
                      <td className="px-4 py-2 text-xs text-[#5C5A55]">
                        {[r.district_name, r.lsgd_name, r.ward_name].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className={`font-mono font-black ${r.wallet_closed ? "text-[#5C5A55]" : r.below_min ? "text-[#DC2626]" : "text-[#15803D]"}`}>
                          ₹{r.wallet_balance.toLocaleString("en-IN")}
                        </div>
                        {r.below_min && !r.wallet_closed && (
                          <div className="text-[9px] uppercase tracking-[0.14em] text-[#DC2626] font-bold mt-0.5">Below min</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right print:hidden">
                        <button
                          onClick={() => openFund(r)}
                          disabled={r.wallet_closed}
                          data-testid={`wallet-addfund-${r.id}`}
                          className="h-8 px-3 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-md text-xs font-semibold inline-flex items-center gap-1"
                        >
                          <Plus size={12} weight="bold" /> Add fund
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add-fund modal */}
      {fundFor && (
        <Modal onClose={() => setFundFor(null)} title={`Add funds — ${fundFor.name}`}>
          <div className="space-y-3">
            <div className="rounded-md border border-[#E5E2D9] bg-[#FEF7F6] p-3 text-sm">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold">Current balance</div>
              <div className="text-2xl font-black text-[#DC2626]">₹{fundFor.wallet_balance.toLocaleString("en-IN")}</div>
              <div className="text-[11px] text-[#5C5A55] mt-0.5">{fundFor.phone || "—"} · {fundFor.customer_code || fundFor.id.slice(0, 8)}</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input type="number" value={fundForm.amount} placeholder="Amount ₹"
                     onChange={(e) => setFundForm(s => ({ ...s, amount: e.target.value }))}
                     data-testid="crm-wallet-addfund-amount"
                     className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              <input type="date" value={fundForm.collection_date}
                     onChange={(e) => setFundForm(s => ({ ...s, collection_date: e.target.value }))}
                     data-testid="crm-wallet-addfund-date"
                     className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
              <select value={fundForm.transaction_mode}
                      onChange={(e) => setFundForm(s => ({ ...s, transaction_mode: e.target.value }))}
                      data-testid="crm-wallet-addfund-mode"
                      className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
                {MODES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <input value={fundForm.transaction_id}
                   onChange={(e) => setFundForm(s => ({ ...s, transaction_id: e.target.value }))}
                   placeholder="Transaction ID (UPI ref, cheque #, etc.)"
                   data-testid="crm-wallet-addfund-txid"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            <input value={fundForm.notes}
                   onChange={(e) => setFundForm(s => ({ ...s, notes: e.target.value }))}
                   placeholder="Notes"
                   className="w-full h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
            <div className="text-[11px] text-[#8A8780] italic">
              Balance updates only after a <b>different</b> staff verifies.
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={submitFund} disabled={fundBusy}
                      data-testid="crm-wallet-addfund-save"
                      className="h-10 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-semibold">
                {fundBusy ? "Submitting…" : "Submit for verification"}
              </button>
              <button onClick={() => setFundFor(null)}
                      className="h-10 border border-[#E5E2D9] rounded-md text-sm">
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Monthly report modal */}
      {reportOpen && (
        <Modal onClose={() => { setReportOpen(false); setReportData(null); }}
               title="Wallet · Monthly usage report" wide>
          <div className="flex items-end gap-2 flex-wrap print:hidden">
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold">Month</label>
              <select value={reportM} onChange={(e) => setReportM(Number(e.target.value))}
                      data-testid="report-month"
                      className="mt-1 h-10 px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
                {MONTHS.map((n, i) => <option key={i} value={i + 1}>{n}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold">Year</label>
              <input type="number" value={reportY}
                     onChange={(e) => setReportY(Number(e.target.value))}
                     data-testid="report-year"
                     className="mt-1 h-10 px-3 w-24 border border-[#E5E2D9] rounded-md text-sm"/>
            </div>
            <button onClick={runReport} disabled={reportBusy}
                    data-testid="report-run"
                    className="h-10 px-4 bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white rounded-md text-sm font-semibold">
              {reportBusy ? "Generating…" : "Generate"}
            </button>
            {reportData && (
              <button onClick={printReport}
                      data-testid="report-print"
                      className="h-10 px-4 border border-[#E5E2D9] rounded-md text-sm font-semibold inline-flex items-center gap-1">
                <Printer size={14} weight="bold" /> Print / PDF
              </button>
            )}
            {(districtId || lsgdId || wardId) && (
              <div className="text-[11px] text-[#8A8780] italic ml-2">
                Filtered by current district/LSGD/ward selection
              </div>
            )}
          </div>

          {/* Report body (also used for print) */}
          {reportData && (
            <div ref={reportRef} className="mt-4">
              <h1>Wallet Usage · {MONTHS[reportData.month - 1]} {reportData.year}</h1>
              <div className="muted text-xs text-[#6B7280]">
                {reportData.window_start.slice(0, 10)} → {reportData.window_end.slice(0, 10)}
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="kpi inline-block px-3 py-2 border border-[#E5E2D9] rounded">
                  Credits <b className="block text-[#15803D] text-base">₹{reportData.totals.credits.toLocaleString("en-IN")}</b>
                </span>
                <span className="kpi inline-block px-3 py-2 border border-[#E5E2D9] rounded">
                  Debits <b className="block text-[#DC2626] text-base">₹{reportData.totals.debits.toLocaleString("en-IN")}</b>
                </span>
                <span className="kpi inline-block px-3 py-2 border border-[#E5E2D9] rounded">
                  Net <b className={`block text-base ${reportData.totals.net >= 0 ? "text-[#15803D]" : "text-[#DC2626]"}`}>
                    ₹{reportData.totals.net.toLocaleString("en-IN")}
                  </b>
                </span>
              </div>

              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">By kind</div>
                <table className="w-full text-xs border border-[#E5E7EB]">
                  <thead>
                    <tr className="bg-[#F7F6F2] text-left text-[10px] uppercase tracking-[0.16em] text-[#5C5A55]">
                      <th className="px-2 py-1">Kind</th>
                      <th className="px-2 py-1 text-right">Credit ₹</th>
                      <th className="px-2 py-1 text-right">Debit ₹</th>
                      <th className="px-2 py-1 text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reportData.totals.by_kind || {}).map(([k, v]) => (
                      <tr key={k} className="border-t border-[#F0EEE7]">
                        <td className="px-2 py-1 capitalize">{kindLabel(k)}</td>
                        <td className="px-2 py-1 text-right credit text-[#15803D] font-mono">₹{v.credit.toLocaleString("en-IN")}</td>
                        <td className="px-2 py-1 text-right debit text-[#DC2626] font-mono">₹{v.debit.toLocaleString("en-IN")}</td>
                        <td className="px-2 py-1 text-right font-mono">{v.count}</td>
                      </tr>
                    ))}
                    {Object.keys(reportData.totals.by_kind || {}).length === 0 && (
                      <tr><td colSpan={4} className="px-2 py-3 text-center text-[#8A8780]">No transactions in this window.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">
                  Top customers ({reportData.rows.length})
                </div>
                <table className="w-full text-xs border border-[#E5E7EB]">
                  <thead>
                    <tr className="bg-[#F7F6F2] text-left text-[10px] uppercase tracking-[0.16em] text-[#5C5A55]">
                      <th className="px-2 py-1">Customer</th>
                      <th className="px-2 py-1">Phone</th>
                      <th className="px-2 py-1 text-right">Credits</th>
                      <th className="px-2 py-1 text-right">Debits</th>
                      <th className="px-2 py-1 text-right">Net</th>
                      <th className="px-2 py-1 text-right">Txns</th>
                      <th className="px-2 py-1 text-right">Ending ₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.rows.map((r) => (
                      <tr key={r.customer_id} className="border-t border-[#F0EEE7]">
                        <td className="px-2 py-1">
                          <div className="font-semibold text-[#111827]">{r.customer_name}</div>
                          {r.customer_code && <div className="text-[10px] font-mono text-[#8A8780]">{r.customer_code}</div>}
                        </td>
                        <td className="px-2 py-1 font-mono">{r.phone || "—"}</td>
                        <td className="px-2 py-1 text-right credit text-[#15803D] font-mono">₹{r.credits.toLocaleString("en-IN")}</td>
                        <td className="px-2 py-1 text-right debit text-[#DC2626] font-mono">₹{r.debits.toLocaleString("en-IN")}</td>
                        <td className={`px-2 py-1 text-right font-mono ${r.net >= 0 ? "text-[#15803D]" : "text-[#DC2626]"}`}>
                          ₹{r.net.toLocaleString("en-IN")}
                        </td>
                        <td className="px-2 py-1 text-right font-mono">{r.txn_count}</td>
                        <td className="px-2 py-1 text-right font-mono">₹{r.ending_balance.toLocaleString("en-IN")}</td>
                      </tr>
                    ))}
                    {reportData.rows.length === 0 && (
                      <tr><td colSpan={7} className="px-2 py-3 text-center text-[#8A8780]">No activity in this window.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {!reportData && !reportBusy && (
            <div className="text-xs text-[#8A8780] mt-4 italic">Pick a month and click Generate.</div>
          )}
        </Modal>
      )}
    </AppShell>
  );
}

/* ─── Tiny helpers ─────────────────────────────────────────────── */

const FilterSelect = ({ value, onChange, options, disabled, ...rest }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      {...rest}
      className="h-10 pl-3 pr-8 border border-[#E5E2D9] rounded-md text-xs bg-white appearance-none disabled:opacity-50 focus:border-[#DC2626] focus:ring-1 focus:ring-[#DC2626] outline-none"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
    <CaretDown size={10} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8A8780] pointer-events-none" weight="bold" />
  </div>
);

const KpiCard = ({ icon: Icon, label, value, sub, tone }) => {
  const tones = {
    red: { bg: "bg-gradient-to-br from-[#FEF7F6] to-[#FDECEA]", ring: "border-[#F4C7B9]", ic: "text-[#DC2626]" },
    amber: { bg: "bg-gradient-to-br from-[#FFF8E6] to-[#FCEDBF]", ring: "border-[#F9E4B7]", ic: "text-[#B45309]" },
    green: { bg: "bg-gradient-to-br from-[#EEF7EC] to-[#D4E0D1]", ring: "border-[#C6D4C2]", ic: "text-[#15803D]" },
    slate: { bg: "bg-white", ring: "border-[#E5E2D9]", ic: "text-[#5C5A55]" },
  };
  const t = tones[tone] || tones.slate;
  return (
    <div className={`rounded-xl border ${t.ring} ${t.bg} p-4`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-white shadow-sm inline-flex items-center justify-center">
          <Icon size={20} weight="fill" className={t.ic} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] font-bold">{label}</div>
          <div className="font-heading text-2xl mt-0.5 text-[#111827]">{value}</div>
          {sub && <div className="text-[11px] text-[#5C5A55] mt-0.5">{sub}</div>}
        </div>
      </div>
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    active: { bg: "bg-[#D4E0D1]", fg: "text-[#1A3317]", label: "Active" },
    approved: { bg: "bg-[#D4E0D1]", fg: "text-[#1A3317]", label: "Active" },
    pending_verification: { bg: "bg-[#F9E4B7]", fg: "text-[#7A5A11]", label: "Pending" },
    pending: { bg: "bg-[#F9E4B7]", fg: "text-[#7A5A11]", label: "Pending" },
    rejected: { bg: "bg-[#F4C7B9]", fg: "text-[#7A2E18]", label: "Rejected" },
  };
  const s = map[status] || { bg: "bg-[#F7F6F2]", fg: "text-[#5C5A55]", label: status || "—" };
  return (
    <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] rounded ${s.bg} ${s.fg} font-semibold`}>
      {s.label}
    </span>
  );
};

const Modal = ({ children, onClose, title, wide }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto print:hidden"
       onClick={onClose}>
    <div
      onClick={(e) => e.stopPropagation()}
      className={`bg-white rounded-xl shadow-2xl w-full ${wide ? "max-w-5xl" : "max-w-lg"} my-8`}
    >
      <div className="flex items-center gap-2 px-5 py-3 border-b border-[#E5E2D9]">
        <div className="font-heading text-lg text-[#111827] flex-1">{title}</div>
        <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-[#F7F6F2] inline-flex items-center justify-center">
          <X size={16} weight="bold" />
        </button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);
